import {useLazyQuery} from '@apollo/client'
import {useEffect, useMemo, useRef} from 'react'
import {Offcanvas, Form, ListGroup} from 'react-bootstrap'
import {useState} from 'react'
import {GET_COMMISSION_REPORT_DETAILS_BY_TERM} from '../../../gql/queries/commissionReportsDetailsApiQueries'
import {BadgeStatus} from './BadgeStatus' // Ajusta la ruta a la tuya
import moment from 'moment'
import {useNavigate} from 'react-router-dom'

interface Props {
  isOpen: boolean
  onClose: () => void
}

const COMMISSION_FINDER_TERM_STORAGE_KEY = 'tfi_commission_finder_term'

export const OffcanvasSearchCommissionDetails = ({isOpen, onClose}: Props) => {
  const [term, setTerm] = useState('')
  const [search, {data, loading}] = useLazyQuery(GET_COMMISSION_REPORT_DETAILS_BY_TERM)
  const navigate = useNavigate()
  const wasOpenRef = useRef(false)
  const sortedResults = useMemo(() => {
    return [...(data?.commissionReportDetailsByTerm || [])].sort((a: any, b: any) => {
      const yearA = Number(a.effectiveYear) || 0
      const yearB = Number(b.effectiveYear) || 0
      const monthA = Number(a.effectiveMonth) || 0
      const monthB = Number(b.effectiveMonth) || 0

      if (yearA !== yearB) return yearB - yearA
      return monthB - monthA
    })
  }, [data?.commissionReportDetailsByTerm])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const storedTerm = window.localStorage.getItem(COMMISSION_FINDER_TERM_STORAGE_KEY)
    if (storedTerm) {
      setTerm(storedTerm)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const normalizedTerm = term.trim()
    if (!normalizedTerm) {
      window.localStorage.removeItem(COMMISSION_FINDER_TERM_STORAGE_KEY)
      return
    }

    window.localStorage.setItem(COMMISSION_FINDER_TERM_STORAGE_KEY, normalizedTerm)
  }, [term])

  useEffect(() => {
    const justOpened = !wasOpenRef.current && isOpen
    wasOpenRef.current = isOpen

    if (!justOpened) return

    const normalizedTerm = term.trim()
    if (normalizedTerm) {
      search({variables: {term: normalizedTerm}})
    }
  }, [isOpen, term, search])

  useEffect(() => {
    if (data) {
      console.log('Resultados:', data.commissionReportDetailsByTerm)
    }
  }, [data])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const normalizedTerm = term.trim()
    if (normalizedTerm) {
      search({variables: {term: normalizedTerm}})
    }
  }

  return (
    <Offcanvas show={isOpen} onHide={onClose} placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Commissions Finder</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form onSubmit={handleSearch} className="mb-3">
          <Form.Control
            type="text"
            placeholder="Search by client or Member ID..."
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            disabled={loading}
          />
        </Form>

        <ListGroup>
          {sortedResults.map((item: any) => {
            const fullAgent = `${item.user?.firstName || ''} ${item.user?.lastName || ''}`
            const effectiveMonthNum = item.effectiveMonth ?? item.commissionReport?.effectiveMonth
            const month = effectiveMonthNum ? moment().month(effectiveMonthNum - 1).format('MMM') : ''
            const amount = `$${parseFloat(item.rate).toFixed(2)} * ${item.members} applicants = $${parseFloat(item.subtotal).toFixed(2)}`

            const reportMonth = item.commissionReport?.effectiveMonth
            const reportYear = item.commissionReport?.effectiveYear
            const displayYear = item.effectiveYear ?? reportYear
            const differsFromReport =
              item.effectiveMonth !== reportMonth || (item.effectiveYear !== null && item.effectiveYear !== reportYear)

            return (
              <ListGroup.Item
                  key={item.id}
                  className="mb-3"
                  style={{cursor: 'pointer'}}
                  onClick={() => {
                    onClose() // <- cierra el offcanvas
                    const detailsBasePath = `/commissions/${item.commissionReport?.id}/details`
                    const detailsClientPath = item.client
                      ? `${detailsBasePath}/${encodeURIComponent(item.client)}`
                      : detailsBasePath
                    const memberIdQuery = item.memberId
                      ? `?memberId=${encodeURIComponent(item.memberId)}`
                      : ''
                    navigate(`${detailsClientPath}${memberIdQuery}`)
                  }}>
                <div className="fw-semibold fs-5">{item.commissionReport?.name}</div>
                <div className="fw-bold text-uppercase">{item.client}</div>
                <div>Agent: {fullAgent} [{item.user?.npn}]</div>
                <div>Member ID: {item.memberId}</div>
                <div>
                  Month: {month} {displayYear}
                  {differsFromReport && (
                    <span className="ms-1" style={{color: '#0095e8'}} title="Período diferente al reporte">•</span>
                  )}
                </div>
                <div className="d-flex align-items-center">
                  Status: <BadgeStatus state={item.status} />
                </div>
                <div>Amount: {amount}</div>
              </ListGroup.Item>
            )
          })}
        </ListGroup>
      </Offcanvas.Body>
    </Offcanvas>
  )
}
